import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api, unwrap } from '../../../api/client';
import { errorMessage } from '../../../api/errors';
import type { components } from '../../../api/schema';
import { useAuth } from '../../../auth/AuthProvider';
import { Badge, Button, Card, Field, Input, Select } from '../../../design';
import { useToast } from '../../../design/Toast';
import type { LockerDetail } from '../common';
import { DevicePanel } from '../DevicePanel';
import s from '../lockers.module.css';

type Controller = components['schemas']['LockerController'];
type Board = components['schemas']['Board'];
type BoardCreate = components['schemas']['BoardCreate'];

export function HardwareTab({ locker: l }: { locker: LockerDetail }) {
  const { has } = useAuth();
  const canEdit = has('hardware');
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['locker', l.id] });
    void qc.invalidateQueries({ queryKey: ['lockers'] });
  };
  const form = useForm<Controller>({ defaultValues: l.controller });
  const saveController = useMutation({
    mutationFn: (c: Controller) =>
      unwrap(
        api.PUT('/lockers/{id}/controller', {
          params: { path: { id: l.id } },
          body: {
            ...c,
            screenInches: Number(c.screenInches),
            resolution: { width: Number(c.resolution.width), height: Number(c.resolution.height) },
            serial: {
              ...c.serial,
              baud: Number(c.serial.baud) as Controller['serial']['baud'],
              dataBits: Number(c.serial.dataBits) as 7 | 8,
              stopBits: Number(c.serial.stopBits) as 1 | 2,
              timeoutMs: Number(c.serial.timeoutMs),
              retries: Number(c.serial.retries),
              pollMs: Number(c.serial.pollMs),
            },
          },
        }),
      ),
    onSuccess: () => {
      toast('Controlerul a fost salvat; versiunea configurației a crescut.', 'success');
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <h2>Controler</h2>
          <form
            className="stack"
            style={{ marginTop: 12 }}
            onSubmit={form.handleSubmit((c) => saveController.mutate(c))}
          >
            <div className={s.formGrid}>
              <Field label="Dispozitiv" htmlFor="c-dev">
                <Input
                  id="c-dev"
                  disabled={!canEdit}
                  {...form.register('deviceLabel', { required: true })}
                />
              </Field>
              <Field label="Sistem de operare" htmlFor="c-os">
                <Input id="c-os" disabled={!canEdit} {...form.register('os')} />
              </Field>
              <Field label="Ecran (inch)" htmlFor="c-inch">
                <Input
                  id="c-inch"
                  type="number"
                  step="0.1"
                  disabled={!canEdit}
                  {...form.register('screenInches')}
                />
              </Field>
              <Field label="Orientare" htmlFor="c-orient">
                <Select id="c-orient" disabled={!canEdit} {...form.register('orientation')}>
                  <option value="portrait">Portret</option>
                  <option value="landscape">Peisaj</option>
                </Select>
              </Field>
              <Field label="Lățime (px)" htmlFor="c-w">
                <Input
                  id="c-w"
                  type="number"
                  disabled={!canEdit}
                  {...form.register('resolution.width')}
                />
              </Field>
              <Field label="Înălțime (px)" htmlFor="c-h">
                <Input
                  id="c-h"
                  type="number"
                  disabled={!canEdit}
                  {...form.register('resolution.height')}
                />
              </Field>
            </div>
            <h3>Magistrala serială</h3>
            <div className={s.formGrid}>
              <Field label="Port" htmlFor="c-port">
                <Input
                  id="c-port"
                  disabled={!canEdit}
                  {...form.register('serial.port', { required: true })}
                />
              </Field>
              <Field label="Baud" htmlFor="c-baud">
                <Select id="c-baud" disabled={!canEdit} {...form.register('serial.baud')}>
                  {[2400, 4800, 9600, 19200, 38400, 57600, 115200].map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Biți / paritate / stop">
                <div className="flex">
                  <Select
                    aria-label="Biți de date"
                    disabled={!canEdit}
                    {...form.register('serial.dataBits')}
                  >
                    <option value={8}>8</option>
                    <option value={7}>7</option>
                  </Select>
                  <Select
                    aria-label="Paritate"
                    disabled={!canEdit}
                    {...form.register('serial.parity')}
                  >
                    <option value="none">fără</option>
                    <option value="even">pară</option>
                    <option value="odd">impară</option>
                  </Select>
                  <Select
                    aria-label="Biți de stop"
                    disabled={!canEdit}
                    {...form.register('serial.stopBits')}
                  >
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                  </Select>
                </div>
              </Field>
              <Field label="Timeout / reîncercări / polling (ms)">
                <div className="flex">
                  <Input
                    aria-label="Timeout ms"
                    type="number"
                    disabled={!canEdit}
                    {...form.register('serial.timeoutMs')}
                  />
                  <Input
                    aria-label="Reîncercări"
                    type="number"
                    disabled={!canEdit}
                    {...form.register('serial.retries')}
                  />
                  <Input
                    aria-label="Polling ms"
                    type="number"
                    disabled={!canEdit}
                    {...form.register('serial.pollMs')}
                  />
                </div>
              </Field>
            </div>
            {canEdit && (
              <div>
                <Button variant="primary" type="submit" busy={saveController.isPending}>
                  Salvează controlerul
                </Button>
              </div>
            )}
          </form>
        </Card>
        <div className="stack">
          <BoardsCard locker={l} canEdit={canEdit} />
          <DevicePanel locker={l} />
        </div>
      </div>
    </div>
  );
}

function BoardsCard({ locker: l, canEdit }: { locker: LockerDetail; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['locker', l.id] });
  const [draft, setDraft] = useState<BoardCreate | null>(null);
  const [edit, setEdit] = useState<Record<string, Partial<BoardCreate>>>({});
  const fail = (e: unknown) => toast(errorMessage(e), 'error');
  const add = useMutation({
    mutationFn: (b: BoardCreate) =>
      unwrap(
        api.POST('/lockers/{id}/boards', {
          params: { path: { id: l.id } },
          body: { ...b, address: Number(b.address), doors: Number(b.doors) },
        }),
      ),
    onSuccess: () => {
      setDraft(null);
      refresh();
    },
    onError: fail,
  });
  const patch = useMutation({
    mutationFn: ({ boardId, body }: { boardId: string; body: Partial<BoardCreate> }) =>
      unwrap(
        api.PATCH('/lockers/{id}/boards/{boardId}', {
          params: { path: { id: l.id, boardId } },
          body: {
            ...body,
            address: body.address !== undefined ? Number(body.address) : undefined,
            doors: body.doors !== undefined ? Number(body.doors) : undefined,
          },
        }),
      ),
    onSuccess: (_b, v) => {
      setEdit((e) => {
        const n = { ...e };
        delete n[v.boardId];
        return n;
      });
      refresh();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (boardId: string) =>
      unwrap(
        api.DELETE('/lockers/{id}/boards/{boardId}', { params: { path: { id: l.id, boardId } } }),
      ),
    onSuccess: refresh,
    onError: fail,
  });
  const tone = (b: Board) =>
    b.status === 'online' ? 'green' : b.status === 'degraded' ? 'orange' : 'red';
  return (
    <Card>
      <div className="flex between">
        <h2>Plăci de relee</h2>
        {canEdit && !draft && (
          <Button
            small
            icon="plus"
            onClick={() =>
              setDraft({
                name: `Placa ${String(l.boards.length + 1).padStart(2, '0')}`,
                address: l.boards.length + 1,
                doors: 12,
                firmware: '',
              })
            }
          >
            Adaugă placă
          </Button>
        )}
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 13 }}
        className={s.boardsTable}
      >
        <thead>
          <tr className="muted small">
            <th style={{ textAlign: 'left' }}>Nume</th>
            <th>Adresă</th>
            <th>Ieșiri</th>
            <th>Firmware</th>
            <th>Stare</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {l.boards.map((b) => {
            const e = edit[b.id];
            return (
              <tr key={b.id}>
                <td>
                  {e ? (
                    <Input
                      aria-label={`Nume ${b.name}`}
                      value={e.name ?? b.name}
                      onChange={(ev) =>
                        setEdit({ ...edit, [b.id]: { ...e, name: ev.target.value } })
                      }
                    />
                  ) : (
                    b.name
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {e ? (
                    <Input
                      aria-label={`Adresă ${b.name}`}
                      type="number"
                      style={{ width: 70 }}
                      value={e.address ?? b.address}
                      onChange={(ev) =>
                        setEdit({ ...edit, [b.id]: { ...e, address: Number(ev.target.value) } })
                      }
                    />
                  ) : (
                    b.address
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {e ? (
                    <Input
                      aria-label={`Ieșiri ${b.name}`}
                      type="number"
                      style={{ width: 70 }}
                      value={e.doors ?? b.doors}
                      onChange={(ev) =>
                        setEdit({ ...edit, [b.id]: { ...e, doors: Number(ev.target.value) } })
                      }
                    />
                  ) : (
                    `${b.mappedDoors}/${b.doors}`
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {e ? (
                    <Input
                      aria-label={`Firmware ${b.name}`}
                      style={{ width: 90 }}
                      value={e.firmware ?? b.firmware}
                      onChange={(ev) =>
                        setEdit({ ...edit, [b.id]: { ...e, firmware: ev.target.value } })
                      }
                    />
                  ) : (
                    b.firmware || '—'
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Badge tone={tone(b)}>{b.status}</Badge>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit &&
                    (e ? (
                      <>
                        <Button
                          small
                          variant="primary"
                          onClick={() => patch.mutate({ boardId: b.id, body: e })}
                          busy={patch.isPending}
                        >
                          Salvează
                        </Button>{' '}
                        <Button
                          small
                          onClick={() =>
                            setEdit((x) => {
                              const n = { ...x };
                              delete n[b.id];
                              return n;
                            })
                          }
                        >
                          Renunță
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          small
                          variant="ghost"
                          icon="edit"
                          aria-label={`Editează ${b.name}`}
                          onClick={() => setEdit({ ...edit, [b.id]: {} })}
                        />
                        <Button
                          small
                          variant="ghost"
                          icon="trash"
                          aria-label={`Elimină ${b.name}`}
                          disabled={l.boards.length <= 1 || b.mappedDoors > 0}
                          title={b.mappedDoors > 0 ? 'Placa are compartimente mapate' : undefined}
                          onClick={() => remove.mutate(b.id)}
                        />
                      </>
                    ))}
                </td>
              </tr>
            );
          })}
          {draft && (
            <tr>
              <td>
                <Input
                  aria-label="Nume placă nouă"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </td>
              <td>
                <Input
                  aria-label="Adresă placă nouă"
                  type="number"
                  style={{ width: 70 }}
                  value={draft.address}
                  onChange={(e) => setDraft({ ...draft, address: Number(e.target.value) })}
                />
              </td>
              <td>
                <Input
                  aria-label="Ieșiri placă nouă"
                  type="number"
                  style={{ width: 70 }}
                  value={draft.doors}
                  onChange={(e) => setDraft({ ...draft, doors: Number(e.target.value) })}
                />
              </td>
              <td>
                <Input
                  aria-label="Firmware placă nouă"
                  style={{ width: 90 }}
                  value={draft.firmware ?? ''}
                  onChange={(e) => setDraft({ ...draft, firmware: e.target.value })}
                />
              </td>
              <td />
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <Button
                  small
                  variant="primary"
                  onClick={() => add.mutate(draft)}
                  busy={add.isPending}
                >
                  Adaugă
                </Button>{' '}
                <Button small onClick={() => setDraft(null)}>
                  Renunță
                </Button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}
